// @flow
import React, { useMemo } from 'react';
import isEmpty from 'ramda/es/isEmpty';
import reduce from 'ramda/es/reduce';
import map from 'ramda/es/map';
import pipe from 'ramda/es/pipe';
import sort from 'ramda/es/sort';
import descend from 'ramda/es/descend';
import prop from 'ramda/es/prop';
import { useTranslation } from 'react-i18next';

import toFixedValue, { toFixedValueCurrency } from 'app/common/lib/toFixedValue';
import { useCurrencySign } from 'app/common/components-next/currency';

import {
  Table,
  HeaderCell,
  HeaderRow,
  Row,
  Cell,
} from 'app/common/components/Table';

import Individual from '../commonComponents/Individual';
import ClientTip from '../commonComponents/ClientTip';
import styles from './ItemDetails.module.scss';;


type Props = {
  data: Array<{
    id: number,
    title: string,
    n: number,
    individual: boolean,
    cost: number,
    total: number,
    day: string,
    time: string,
    year: string,
    guid: string,
    createdBy: string,
  }>,
}

const ItemDetails = ({ data }: Props) => {
  const { t } = useTranslation('cafeteria');
  const currency = useCurrencySign();
  const currencyComma = `,\u00A0${currency}`;
  const tableHeaders = t('schedule_details_table_columns', { returnObjects: true });

  const totalSum = useMemo(() => toFixedValueCurrency(
    reduce((sum, { total: tt }) => (sum + tt), 0, data),
    false,
    currency,
  ), [data, currency]);

  const items = useMemo(() => pipe(
    sort(descend(prop('time'))),
    map(({ cost, total, ...other }) => ({
      cost: toFixedValue(cost),
      total: toFixedValue(total),
      ...other,
    })),
  )(data), [data]);

  if (isEmpty(data)) {
    return (<h3 className={styles.header}>
      {t('schedule_details_table_empty', 'Нет покупок в этот день')}
    </h3>);
  }

  const { day, year } = data[0];

  return (
    <div>
      <h3 className={styles.header}>
        <span>{t('schedule_details_table_title', { day, year })}</span>
        <span className={styles.totalSum}>
          {`${t('schedule_details_table_title_total', 'Итого')}: ${totalSum}`}
        </span>
      </h3>
      <Table>
        <HeaderRow>
          <HeaderCell>
            {tableHeaders[0]}
          </HeaderCell>
          <HeaderCell>
            {tableHeaders[1]}
          </HeaderCell>
          <HeaderCell>
            {tableHeaders[2]}
          </HeaderCell>
          <HeaderCell grow={3}>
            {tableHeaders[3]}
          </HeaderCell>
          <HeaderCell>
            {tableHeaders[4]}
          </HeaderCell>
          <HeaderCell>
            {tableHeaders[5]}
            {currencyComma}
          </HeaderCell>
          <HeaderCell>
            {tableHeaders[6]}
            {currencyComma}
          </HeaderCell>
        </HeaderRow>

        {items.map(({ id, title, n, cost, total, time, individual, createdBy }) => (
          <Row key={id} className={styles.row}>
            <Cell>{time}</Cell>
            <Cell>
              <ClientTip client={createdBy}>{id}</ClientTip>
            </Cell>
            <Cell className={styles.iconCell}>
              <Individual individual={individual} />
            </Cell>
            <Cell grow={3}>{title}</Cell>
            <Cell>{n}</Cell>
            <Cell>{cost}</Cell>
            <Cell>{total}</Cell>
          </Row>
        ))}
      </Table>
    </div>
  );
};

export default ItemDetails;
